/**
 * @file footer-layout.ts
 * @description Раскладка подвала «Колонками» — один подсчёт колонок для
 * кабинета (схема и счётчик «N из 3»), сервера (лимит меню, скрытие
 * ссылок-дублей) и витрины (phase19.16, приёмка Р2а; SB113 — колонки задаёт
 * меню, пустая колонка считается в лимите, но на сайте не видна; SB114 —
 * колонка «О магазине»; SB115 — дубли ссылок платформы; uiux-footer-columns
 * §0 Ф1, §2, §5).
 *
 * До этого колонки считались в трёх местах по-разному (Ф1): кабинет и сервер
 * — группы и автосписок вместе с пустыми; витрина — без пустых, зато с
 * лишней колонкой из одиночных ссылок, которую лимит не видел.
 *
 * Чистая функция над деревом меню подвала любого из трёх видов:
 *   - узлы снимка `SiteMenuNode` (сервер, проверка меню);
 *   - пункты кабинета (`SiteMenuItemDto` — с id и `target`);
 *   - готовые пункты витрины `PublicMenuItem` (у них нет `linkType`:
 *     группа — пункт без адреса).
 * Чего функция знать не может (опубликована ли цель, сколько категорий в
 * автосписке, адреса ссылок платформы), передаёт вызывающий — параметрами.
 *
 * Одиночные ссылки верхнего уровня (`loose`) собираются в одну колонку без
 * заголовка — переходный период до data-миграции П3 (SB113), после неё их
 * в подвале нет.
 */

import { FOOTER_ACCOUNT_HREF, type FooterDeduplicatedLink } from "./footer-legal.js";
import { SITE_LIMITS } from "./limits.js";
import type { SiteMenuLinkType, SiteSystemRoute } from "./menu.js";

/**
 * Минимум полей пункта, которые читает раскладка. `C` — тип детей (тот же
 * тип пункта), чтобы колонки возвращали пункты вызывающего без приведения.
 */
export interface FooterLayoutNode<C> {
  /** id строки черновика (кабинет); у снимка и витрины нет. */
  id?: string | null;
  label: string | null;
  /** У снимка и кабинета есть; у пункта витрины нет — группа = пункт без адреса. */
  linkType?: SiteMenuLinkType;
  /** Готовый адрес (витрина, сервер после сборки). */
  href?: string | null;
  url?: string | null;
  sitePageId?: string | null;
  legalDocumentId?: string | null;
  systemRoute?: SiteSystemRoute | null;
  autoLimit?: number | null;
  /** SB81. */
  isHidden?: boolean;
  children: readonly C[];
}

/** Вид колонки: группа меню, автосписок категорий, одиночные ссылки без заголовка. */
export type FooterColumnKind = "group" | "auto-categories" | "loose";

export interface FooterLayoutColumn<T> {
  /** id группы/автосписка (кабинет); у колонки одиночных ссылок и у снимка — null. */
  id: string | null;
  /** Заголовок колонки; null — без заголовка. */
  title: string | null;
  kind: FooterColumnKind;
  /** Пункт меню, задающий колонку; у колонки одиночных ссылок — null. */
  node: T | null;
  /** Пункты колонки как есть (вместе со скрытыми; у автосписка снимка — пусто). */
  items: T[];
  /** Сколько пунктов покупатель увидит на сайте. */
  visibleCount: number;
  /** Колонка скрыта сама (SB81): в лимите, на сайте нет. */
  hidden: boolean;
  /** Видимых пунктов нет: в лимите, на сайте нет, в кабинете помечена (SB113). */
  empty: boolean;
}

/** Части колонки «О магазине» в порядке показа (SB114). */
export const FOOTER_ABOUT_PARTS = ["brand", "tagline", "contacts", "hours", "signature"] as const;
export type FooterAboutPart = (typeof FOOTER_ABOUT_PARTS)[number];

/** Что известно о колонке «О магазине»: флаги `footer.show` и данные бренда. */
export interface FooterAboutInput {
  show: { brand: boolean; tagline: boolean; contacts: boolean; signature: boolean };
  /** Есть название сайта или логотип. */
  hasBrand: boolean;
  tagline: string | null;
  /** Контактов с «показывать в подвале» и верным значением. */
  footerContacts: number;
  workingHours: string | null;
  signature: string | null;
}

/**
 * Цель ссылки платформы для сравнения «по цели» (SB115). Совпадение по
 * любому из заданных полей; `hrefs` сравниваются без хвостового «/» и «#…».
 */
export interface FooterPlatformTarget {
  sitePageId?: string | null;
  legalDocumentId?: string | null;
  systemRoute?: SiteSystemRoute | null;
  hrefs?: ReadonlyArray<string | null>;
}

/**
 * Цели ссылок платформы, которые прячутся при дубле. «Личный кабинет» есть
 * всегда (`FOOTER_ACCOUNT_HREF`) — его цель подставляется сама; null —
 * ссылки нет (документ или страница не опубликованы).
 */
export type FooterPlatformTargets = Partial<Record<FooterDeduplicatedLink, FooterPlatformTarget | null>>;

export interface FooterLayoutOptions<T> {
  /**
   * Виден ли пункт покупателю. По умолчанию — не скрыт (SB81). Снятую или
   * удалённую цель (SB16) знает только вызывающий: кабинет — по
   * `target.status`, сервер — по загруженным целям.
   */
  isVisible?: (node: T) => boolean;
  /** Адрес пункта для сравнения со ссылками платформы; по умолчанию `href ?? url`. */
  hrefOf?: (node: T) => string | null;
  /** Сколько категорий есть для автосписка; не задано — число детей узла. */
  catalogCategoriesCount?: number;
  /** Цели ссылок платформы (SB115). */
  platformLinks?: FooterPlatformTargets;
  /** Показывается ли меню в подвале (`footer.show.menu`); выключено — колонок на сайте нет, дублей нет. */
  menuShown?: boolean;
  /** Данные колонки «О магазине»; не заданы — колонка не считается. */
  about?: FooterAboutInput;
}

/** Пункт меню, ведущий туда же, куда ссылка платформы (SB115). */
export interface FooterDuplicate {
  /** id пункта (кабинет); у снимка и витрины — null, тогда смотрите `column`/`item`. */
  itemId: string | null;
  platformLink: FooterDeduplicatedLink;
  /** Номер колонки в `columns`. */
  column: number;
  /** Номер пункта в `columns[column].items`. */
  item: number;
}

export interface FooterLayout<T> {
  about: { visible: boolean; parts: FooterAboutPart[] };
  /** Колонки меню по порядку — все, включая пустые и скрытые. */
  columns: Array<FooterLayoutColumn<T>>;
  /** Сколько колонок меню видно на сайте. */
  visibleColumns: number;
  /** Сколько колонок без видимых пунктов. */
  emptyColumns: number;
  /** SITE_LIMITS.footerColumns. */
  limit: number;
  /** Колонок (вместе с пустыми) больше лимита. */
  overLimit: boolean;
  menuShown: boolean;
  duplicates: FooterDuplicate[];
}

const notHidden = (node: { isHidden?: boolean }): boolean => node.isHidden !== true;

/** Адрес для сравнения: без пробелов по краям, «#…» и хвостового «/» (кроме корня). */
export function normalizeFooterHref(href: string): string {
  const noHash = href.trim().split("#")[0] ?? "";
  return noHash.length > 1 && noHash.endsWith("/") ? noHash.replace(/\/+$/, "") || "/" : noHash;
}

function columnKind(node: FooterLayoutNode<unknown>): FooterColumnKind {
  if (node.linkType !== undefined) {
    if (node.linkType === "GROUP") return "group";
    if (node.linkType === "CATALOG_CATEGORIES") return "auto-categories";
    return "loose";
  }
  // Пункт витрины: колонка — пункт без адреса (группа или развёрнутый автосписок).
  return node.href === null || node.href === undefined ? "group" : "loose";
}

function aboutParts(about: FooterAboutInput | undefined): FooterAboutPart[] {
  if (!about) return [];
  const filled = (v: string | null): boolean => v !== null && v.trim() !== "";
  const has: Record<FooterAboutPart, boolean> = {
    brand: about.show.brand && about.hasBrand,
    tagline: about.show.tagline && filled(about.tagline),
    contacts: about.show.contacts && about.footerContacts > 0,
    // Часы работы идут вместе с контактами — отдельного флага нет (как на витрине).
    hours: about.show.contacts && filled(about.workingHours),
    signature: about.show.signature && filled(about.signature),
  };
  return FOOTER_ABOUT_PARTS.filter((part) => has[part]);
}

function matchesTarget<T extends FooterLayoutNode<T>>(
  node: T,
  href: string | null,
  target: FooterPlatformTarget,
): boolean {
  if (target.sitePageId && node.sitePageId === target.sitePageId) return true;
  if (target.legalDocumentId && node.legalDocumentId === target.legalDocumentId) return true;
  if (target.systemRoute && node.systemRoute === target.systemRoute) return true;
  if (href === null || !target.hrefs) return false;
  const own = normalizeFooterHref(href);
  return target.hrefs.some((h) => h !== null && normalizeFooterHref(h) === own);
}

/**
 * Раскладка подвала: колонки меню (в лимите и пустые — SB113), «О магазине»
 * (SB114) и пункты-дубли ссылок платформы (SB115).
 */
export function footerLayout<T extends FooterLayoutNode<T>>(
  nodes: readonly T[],
  options: FooterLayoutOptions<T> = {},
): FooterLayout<T> {
  const isVisible = options.isVisible ?? notHidden;
  const hrefOf = options.hrefOf ?? ((n: T): string | null => n.href ?? n.url ?? null);
  const menuShown = options.menuShown ?? true;

  const columns: Array<FooterLayoutColumn<T>> = [];
  let loose: FooterLayoutColumn<T> | null = null;
  for (const node of nodes) {
    const kind = columnKind(node);
    if (kind === "loose") {
      // Одна колонка без заголовка — на месте первой одиночной ссылки (как витрина до SB113).
      if (!loose) {
        loose = { id: null, title: null, kind, node: null, items: [], visibleCount: 0, hidden: false, empty: true };
        columns.push(loose);
      }
      loose.items.push(node);
      continue;
    }
    const title = node.label?.trim() ? node.label.trim() : null;
    columns.push({
      id: node.id ?? null,
      title,
      kind,
      node,
      items: [...node.children],
      visibleCount: 0,
      hidden: !isVisible(node),
      empty: true,
    });
  }

  for (const column of columns) {
    let count = column.items.filter(isVisible).length;
    if (column.kind === "auto-categories" && options.catalogCategoriesCount !== undefined) {
      const limit = column.node?.autoLimit ?? SITE_LIMITS.catalogCategoriesAutoLimit;
      count = Math.min(limit, Math.max(0, options.catalogCategoriesCount));
    }
    column.visibleCount = count;
    column.empty = count === 0;
  }

  const shown = (c: FooterLayoutColumn<T>): boolean => menuShown && !c.hidden && !c.empty;

  const targets: FooterPlatformTargets = {
    account: { hrefs: [FOOTER_ACCOUNT_HREF] },
    ...options.platformLinks,
  };
  const duplicates: FooterDuplicate[] = [];
  columns.forEach((column, ci) => {
    // Автосписок ведёт только на категории — ссылкой платформы он быть не может.
    if (!shown(column) || column.kind === "auto-categories") return;
    column.items.forEach((item, ii) => {
      if (!isVisible(item)) return;
      const href = hrefOf(item);
      for (const [link, target] of Object.entries(targets) as Array<[FooterDeduplicatedLink, FooterPlatformTarget | null | undefined]>) {
        if (target && matchesTarget(item, href, target)) {
          duplicates.push({ itemId: item.id ?? null, platformLink: link, column: ci, item: ii });
        }
      }
    });
  });

  const parts = aboutParts(options.about);
  const limit = SITE_LIMITS.footerColumns;
  return {
    about: { visible: parts.length > 0, parts },
    columns,
    visibleColumns: columns.filter(shown).length,
    emptyColumns: columns.filter((c) => c.empty).length,
    limit,
    overLimit: columns.length > limit,
    menuShown,
    duplicates,
  };
}

/** Ссылки платформы, которые прячутся в нижней полосе из-за дублей (SB115). */
export function footerHiddenPlatformLinks(layout: Pick<FooterLayout<unknown>, "duplicates">): Set<FooterDeduplicatedLink> {
  return new Set(layout.duplicates.map((d) => d.platformLink));
}
